/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getRadiationTypes } from 'app/entities/radiation-type/radiation-type.reducer';
import { getEntities as getFrequencies } from 'app/entities/frequency/frequency.reducer';
import { getEntities as getSequences } from 'app/entities/sequence/sequence.reducer';
import { getEntities as getDosageGuidelines } from 'app/entities/dosage-guideline/dosage-guideline.reducer';
import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getOrganAtRisks } from 'app/entities/organ-at-risk/organ-at-risk.reducer';
import { getEntities as getRadiationConstraints } from 'app/entities/radiation-constraint/radiation-constraint.reducer';
import { getEntities as getRegimenCycles } from 'app/entities/regimen-cycle/regimen-cycle.reducer';
import { createEntity, getEntity, reset, updateEntity } from './radiation-session.reducer';

export const RadiationSessionUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const radiationTypes = useAppSelector(state => state.radiationType.entities);
  const frequencies = useAppSelector(state => state.frequency.entities);
  const sequences = useAppSelector(state => state.sequence.entities);
  const dosageGuidelines = useAppSelector(state => state.dosageGuideline.entities);
  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const organAtRisks = useAppSelector(state => state.organAtRisk.entities);
  const radiationConstraints = useAppSelector(state => state.radiationConstraint.entities);
  const regimenCycles = useAppSelector(state => state.regimenCycle.entities);
  const radiationSessionEntity = useAppSelector(state => state.radiationSession.entity);
  const loading = useAppSelector(state => state.radiationSession.loading);
  const updating = useAppSelector(state => state.radiationSession.updating);
  const updateSuccess = useAppSelector(state => state.radiationSession.updateSuccess);

  const handleClose = () => {
    navigate(`/radiation-session${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getRadiationTypes({}));
    dispatch(getFrequencies({}));
    dispatch(getSequences({}));
    dispatch(getDosageGuidelines({}));
    dispatch(getClinicalDictionaries({}));
    dispatch(getOrganAtRisks({}));
    dispatch(getRadiationConstraints({}));
    dispatch(getRegimenCycles({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...radiationSessionEntity,
      ...values,
      radiation: radiationTypes.find(it => it.id.toString() === values.radiation?.toString()),
      frequency: frequencies.find(it => it.id.toString() === values.frequency?.toString()),
      sequence: sequences.find(it => it.id.toString() === values.sequence?.toString()),
      dose: dosageGuidelines.find(it => it.id.toString() === values.dose?.toString()),
      fractionationScheme: clinicalDictionaries.find(it => it.id.toString() === values.fractionationScheme?.toString()),
      targetVolume: clinicalDictionaries.find(it => it.id.toString() === values.targetVolume?.toString()),
      organs: mapIdList(values.organs),
      constraints: mapIdList(values.constraints),
      cycle: regimenCycles.find(it => it.id.toString() === values.cycle?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...radiationSessionEntity,
          radiation: radiationSessionEntity?.radiation?.id,
          frequency: radiationSessionEntity?.frequency?.id,
          sequence: radiationSessionEntity?.sequence?.id,
          dose: radiationSessionEntity?.dose?.id,
          fractionationScheme: radiationSessionEntity?.fractionationScheme?.id,
          targetVolume: radiationSessionEntity?.targetVolume?.id,
          organs: radiationSessionEntity?.organs?.map(e => e.id.toString()),
          constraints: radiationSessionEntity?.constraints?.map(e => e.id.toString()),
          cycle: radiationSessionEntity?.cycle?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.radiationSession.home.createOrEditLabel" data-cy="RadiationSessionCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.radiationSession.home.createOrEditLabel">Create or edit a RadiationSession</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="radiation-session-id"
                  label={translate('tinyMedicRestApp.radiationSession.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.name')}
                id="radiation-session-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.description')}
                id="radiation-session-description"
                name="description"
                data-cy="description"
                type="text"
                validate={{
                  maxLength: { value: 3000, message: translate('entity.validation.maxlength', { max: 3000 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.totalDose')}
                id="radiation-session-totalDose"
                name="totalDose"
                data-cy="totalDose"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.active')}
                id="radiation-session-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.cursor')}
                id="radiation-session-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.lastUpdated')}
                id="radiation-session-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.microSec')}
                id="radiation-session-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.timestampEntry')}
                id="radiation-session-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="radiation-session-radiation"
                name="radiation"
                data-cy="radiation"
                label={translate('tinyMedicRestApp.radiationSession.radiation')}
                type="select"
              >
                <option value="" key="0" />
                {radiationTypes
                  ? radiationTypes.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-frequency"
                name="frequency"
                data-cy="frequency"
                label={translate('tinyMedicRestApp.radiationSession.frequency')}
                type="select"
              >
                <option value="" key="0" />
                {frequencies
                  ? frequencies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-sequence"
                name="sequence"
                data-cy="sequence"
                label={translate('tinyMedicRestApp.radiationSession.sequence')}
                type="select"
              >
                <option value="" key="0" />
                {sequences
                  ? sequences.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-dose"
                name="dose"
                data-cy="dose"
                label={translate('tinyMedicRestApp.radiationSession.dose')}
                type="select"
              >
                <option value="" key="0" />
                {dosageGuidelines
                  ? dosageGuidelines.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-fractionationScheme"
                name="fractionationScheme"
                data-cy="fractionationScheme"
                label={translate('tinyMedicRestApp.radiationSession.fractionationScheme')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-targetVolume"
                name="targetVolume"
                data-cy="targetVolume"
                label={translate('tinyMedicRestApp.radiationSession.targetVolume')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.organ')}
                id="radiation-session-organ"
                data-cy="organ"
                type="select"
                multiple
                name="organs"
              >
                <option value="" key="0" />
                {organAtRisks
                  ? organAtRisks.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationSession.constraint')}
                id="radiation-session-constraint"
                data-cy="constraint"
                type="select"
                multiple
                name="constraints"
              >
                <option value="" key="0" />
                {radiationConstraints
                  ? radiationConstraints.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-session-cycle"
                name="cycle"
                data-cy="cycle"
                label={translate('tinyMedicRestApp.radiationSession.cycle')}
                type="select"
              >
                <option value="" key="0" />
                {regimenCycles
                  ? regimenCycles.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/radiation-session" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RadiationSessionUpdate;
